import React, { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { getProductValidationSchema } from '../../../validations/LineItemValidation';
import { Divider, Grid, Paper, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import UOMSelect from '../../../components/form/UOMSelect';
import Button from '@mui/material/Button';
import CustomNumberInput from '../../../components/CustomNumberInput';
import CustomTextInput from '../../../components/CustomTextInput';
import TaxCategoryFields from '../../../components/TaxCategoryField';
import GoodsOrServiceTypeSelect from '../../../components/form/GoodOrServiceTypeSelect';
import ItemClassificationFields from '../../../components/form/ItemClassificationFields';
import { useMutateProductHandler } from '../dataHandlers/mutateProductHandler';
import { useSelector } from 'react-redux';
import { getUOMOptionByCode } from '../../../api/codelist/codeListsSlice';
import { map } from 'lodash';
import { DEFAULT_TAX_SCHEME } from '../../../constants/defaults';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';

const REVERSE_CHARGE_TAX_CATEGORY_ID = 'AE';

const createClassifiedTaxCategory = (source = {}) => ({
  id: source?.id ?? source?.id ?? null,
  percent: Number(source?.percent ?? source?.tax_rate ?? 0) || 0,
  tax_exemption_reason_code: source?.tax_exemption_reason_code ?? null,
  tax_exemption_reason: source?.tax_exemption_reason ?? null,
  tax_scheme: source?.tax_scheme ?? DEFAULT_TAX_SCHEME,
});

const createEmptyProduct = () => ({
  unit_of_measure: null,
  name: '',
  description: '',
  quantity: 1,
  unit_price: 1,
  commodity_code: null,
  item_classification_identifier: null,
  service_accounting_code: null,
  nature_code: null,
  classified_tax_category: createClassifiedTaxCategory(),
});

const buildFormValues = (data = {}, uomOption) => {
  const baseData = data ?? {};
  const classified = createClassifiedTaxCategory(baseData.classified_tax_category);

  const commCode =
    baseData.commodity_code?.value ||
    baseData.commodity_code ||
    baseData.commodity_classification?.commodity_code ||
    null;

  const hsn =
    baseData.item_classification_identifier || baseData.commodity_classification?.item_classification_code || null;

  const sacEntry = baseData.additional_item_identification?.find((i) => i.scheme_id === 'SAC');
  const sac =
    baseData.service_accounting_code || sacEntry?.id || baseData.additional_item_identification?.[0]?.id || null;

  const natureCode = baseData.nature_code || baseData.commodity_classification?.nature_code || null;

  return {
    ...createEmptyProduct(),
    ...baseData,
    unit_of_measure: uomOption ?? baseData.unit_of_measure ?? null,
    commodity_code: commCode,
    item_classification_identifier: hsn,
    service_accounting_code: sac,
    nature_code: natureCode,
    classified_tax_category: classified,
  };
};

const ProductsForm = ({ handleClose, initialData, readOnly = false, edit = false }) => {
  const { validations, products: p, eInvoiceForm } = useTranslatedMessages();
  const formT = p.form ?? {};
  const tLine = eInvoiceForm?.line_item_form ?? {};
  const productSchema = useMemo(() => getProductValidationSchema(validations), [validations]);

  const normalizedInitialData = useMemo(() => initialData ?? createEmptyProduct(), [initialData]);
  const uomOption = useSelector(getUOMOptionByCode(normalizedInitialData.unit_of_measure));
  const defaultValues = useMemo(
    () => buildFormValues(normalizedInitialData, uomOption),
    [normalizedInitialData, uomOption],
  );

  const {
    handleSubmit,
    watch,
    control,
    setValue,
    reset,
    getValues,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: yupResolver(productSchema),
  });

  const onProductCreated = ({ addOneMore }) => {
    if (addOneMore) {
      reset(buildFormValues(createEmptyProduct(), null));
    } else {
      handleClose();
    }
  };

  const { onSubmit, error, isPending } = useMutateProductHandler({ onSuccess: onProductCreated });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset isSubmitting when mutation completes
  useEffect(() => {
    if (!isPending && isSubmitting) {
      setIsSubmitting(false);
    }
  }, [isPending, isSubmitting]);

  useEffect(() => {
    map(error, (value, key) => {
      setError(key, { message: value, type: 'string' });
    });
  }, [setError, error]);
  const itemType = watch('commodity_code');
  const taxCategoryId = useWatch({ control, name: 'classified_tax_category.id' });

  useEffect(() => {
    if (taxCategoryId !== REVERSE_CHARGE_TAX_CATEGORY_ID) {
      setValue('nature_code', null);
    }
  }, [taxCategoryId, setValue]);

  const handleSave = async (data, addOneMore) => {
    setIsSubmitting(true);

    const commodityCode = data.commodity_code?.value || data.commodity_code || null;
    const hsn = data.item_classification_identifier?.code || data.item_classification_identifier || null;
    const sac = data.service_accounting_code?.code || data.service_accounting_code || null;
    const classifiedTaxCategory = createClassifiedTaxCategory(data.classified_tax_category ?? data);
    const isReverseCharge = classifiedTaxCategory.id === REVERSE_CHARGE_TAX_CATEGORY_ID;
    const natureCode = isReverseCharge ? data.nature_code : null;

    const itemData = {
      ...data,
      unit_of_measure: data.unit_of_measure?.code || data.unit_of_measure || null,
      classified_tax_category: classifiedTaxCategory,

      ...(commodityCode || hsn || natureCode
        ? {
            commodity_classification: {
              ...(commodityCode && { commodity_code: commodityCode }),
              ...(hsn && {
                item_classification_code: hsn,
                item_classification_list_id: 'HS',
              }),
              ...(natureCode && { nature_code: natureCode }),
            },
          }
        : {}),

      additional_item_identification: sac
        ? [
            {
              id: sac,
              scheme_id: 'SAC',
              scheme_version_id: '1.0',
            },
          ]
        : [],
    };

    delete itemData.commodity_code;
    delete itemData.item_classification_identifier;
    delete itemData.service_accounting_code;
    delete itemData.nature_code;
    delete itemData.product;

    onSubmit(itemData, addOneMore);
  };

  const handleSinglePopupFormSubmit = async (data) => {
    await handleSave(data, false);
  };

  const handleMultiplePopupFormSubmit = async (data) => {
    await handleSave(data, true);
  };

  return (
    <>
      <Paper elevation={0} sx={{ p: '1rem', overflow: 'auto' }}>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            {/* Item Name */}
            <Grid size={{ xs: 6 }}>
              <CustomTextInput
                control={control}
                errors={errors}
                data-testid="name-input"
                name="name"
                readOnly={readOnly}
                label={formT.item_name ?? 'Item Name'}
              />
            </Grid>

            <Grid size={{ xs: 6 }}>
              <CustomTextInput
                data-testid="description-input"
                control={control}
                errors={errors}
                name="description"
                readOnly={readOnly}
                label={formT.description ?? 'Description'}
              />
            </Grid>

            <ItemClassificationFields
              control={control}
              errors={errors}
              readOnly={readOnly}
              watch={watch}
              setValue={setValue}
              edit={edit}
            />
            {!itemType && <Grid size={6} />}
            <Grid size={{ xs: 4 }}>
              <UOMSelect
                control={control}
                errors={errors}
                data-testid="unit-of-measure-select"
                name="unit_of_measure"
                readOnly={readOnly}
              />
            </Grid>

            {/* Quantity */}
            <Grid size={{ xs: 4 }}>
              <CustomNumberInput
                control={control}
                errors={errors}
                data-testid="quantity-input"
                name="quantity"
                readOnly={readOnly}
                label={formT.quantity ?? 'Quantity'}
              />
            </Grid>

            {/* Rate */}
            <Grid size={{ xs: 4 }}>
              <CustomNumberInput
                control={control}
                errors={errors}
                data-testid="unit-price-input"
                name="unit_price"
                readOnly={readOnly}
                label={formT.unit_price ?? 'Unit Price'}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mb: '1.5rem', pt: '1rem' }}>
            {formT.taxes ?? 'Taxes'}
            <Divider></Divider>
          </Typography>

          <Grid container spacing={2} mt={1}>
            <TaxCategoryFields
              control={control}
              errors={errors}
              data-testid="tax-category-select"
              taxCategoryName="classified_tax_category.id"
              taxRateName="classified_tax_category.percent"
              setValue={setValue}
              taxCategoryReadOnly={readOnly}
            />

            {taxCategoryId === REVERSE_CHARGE_TAX_CATEGORY_ID && (
              <Grid size={{ xs: 12 }} mt={2}>
                <GoodsOrServiceTypeSelect
                  control={control}
                  name="nature_code"
                  label={tLine.goods_or_service_type_label ?? 'Goods or Service Type'}
                  error={!!errors?.nature_code}
                  helperText={typeof errors?.nature_code?.message === 'string' ? errors.nature_code.message : ''}
                  readOnly={readOnly}
                />
              </Grid>
            )}
          </Grid>
        </Box>
      </Paper>
      <Divider />
      <Box
        sx={{
          px: 2,
          py: 2,
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
        }}
      >
        {!readOnly && (
          <>
            <Button
              data-testid="save-and-add-new-product-button"
              type="button"
              variant="contained"
              color="primary"
              onClick={handleSubmit(handleMultiplePopupFormSubmit)}
              disabled={isPending || isSubmitting}
            >
              {formT.save_and_add_new ?? 'Save & Add new'}
            </Button>
            <Button
              data-testid="save-product-button"
              type="button"
              variant="contained"
              color="primary"
              onClick={handleSubmit(handleSinglePopupFormSubmit)}
              disabled={isPending || isSubmitting}
            >
              {formT.save ?? 'Save'}
            </Button>
          </>
        )}
      </Box>
    </>
  );
};

export default ProductsForm;
